import z from 'zod';
import { AllCharLabelSchema, UUIDSchema, UnixTimestampSchema } from '../common/schemas';
import {
  DBJsonObjectSchema,
  DBSha256Schema,
  DBTimestampSchema,
  JsonObjectSchema,
  Sha256Schema,
} from '../common/sql';
import { ProcessingStatus } from '../sourceDocuments/enums';

/**
 * DB Schemas
 */

export const DBCategorisationRunIdSchema = UUIDSchema;
export const DBCategorisationRunEngineNameSchema = z.string().min(1).max(128);
export const DBCategorisationRunEngineVersionSchema = z.string().min(1).max(64);
export const DBCategorisationRunModelSchema = z.string().min(1).max(128).nullable();
/** A run moves through the same six states as documents and extraction runs. */
export const DBCategorisationRunStatusSchema = z.enum(ProcessingStatus);
export const DBCategorisationRunStatusDetailSchema = AllCharLabelSchema.min(1).max(2048).nullable();

export const DBCategorisationRunSchema = z.object({
  id: DBCategorisationRunIdSchema,
  engineName: DBCategorisationRunEngineNameSchema,
  engineVersion: DBCategorisationRunEngineVersionSchema,
  model: DBCategorisationRunModelSchema,
  config: DBJsonObjectSchema,
  configHash: DBSha256Schema,
  scope: DBJsonObjectSchema,
  scopeHash: DBSha256Schema,
  status: DBCategorisationRunStatusSchema,
  statusDetail: DBCategorisationRunStatusDetailSchema,
  isActive: z.boolean(),
  stats: DBJsonObjectSchema,
  startedAt: DBTimestampSchema.nullable(),
  finishedAt: DBTimestampSchema.nullable(),
  createdBy: UUIDSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

/**
 * Client Schemas
 */

export const CategorisationRunIdSchema = UUIDSchema;
export const CategorisationRunEngineNameSchema = DBCategorisationRunEngineNameSchema.openapi({
  description: 'The engine that produced the run',
  example: 'categorisation',
});
export const CategorisationRunEngineVersionSchema = DBCategorisationRunEngineVersionSchema;
export const CategorisationRunModelSchema = DBCategorisationRunModelSchema;
export const CategorisationRunStatusSchema = z.enum(ProcessingStatus).openapi({
  description: 'Where the run is in its pass over the questions',
  example: ProcessingStatus.Processed,
});
export const CategorisationRunStatusDetailSchema = DBCategorisationRunStatusDetailSchema;
export const CategorisationRunConfigHashSchema = Sha256Schema.openapi({
  description:
    'Digest of the configuration. With the scope digest, the run identity a repeat lands on.',
  example: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
});
export const CategorisationRunScopeHashSchema = Sha256Schema;
export const CategorisationRunScopeSchema = JsonObjectSchema.openapi({
  description:
    'Which questions the run covered. Part of the run identity, so a deliberate re-run over the same questions varies this.',
});

export const CategorisationRunSchema = z.object({
  id: CategorisationRunIdSchema,
  engineName: CategorisationRunEngineNameSchema,
  engineVersion: CategorisationRunEngineVersionSchema,
  model: CategorisationRunModelSchema,
  config: JsonObjectSchema,
  configHash: CategorisationRunConfigHashSchema,
  scope: CategorisationRunScopeSchema,
  scopeHash: CategorisationRunScopeHashSchema,
  status: CategorisationRunStatusSchema,
  statusDetail: CategorisationRunStatusDetailSchema,
  isActive: z.boolean(),
  stats: JsonObjectSchema,
  startedAt: UnixTimestampSchema.nullable(),
  finishedAt: UnixTimestampSchema.nullable(),
  createdBy: UUIDSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});
